/**
 * RiskManagerService.js
 * Service for communicating with risk manager nodes
 */

// This would be a real API call in production
const fetchRiskData = async (portfolioId = null) => {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 800));
  
  // Mock data that would come from the risk manager nodes
  const riskData = {
    portfolioSummary: getMockPortfolioSummary(portfolioId),
    positionRisks: getMockPositionRisks(portfolioId),
    riskMetrics: getMockRiskMetrics(portfolioId),
    riskSettings: getMockRiskSettings(portfolioId),
    riskAlerts: getMockRiskAlerts(portfolioId),
    updatedAt: new Date().toISOString()
  };
  
  return riskData;
};

// Mock data helpers
const getMockPortfolioSummary = (portfolioId) => {
  // Generate slightly different values based on portfolioId to make it look realistic
  const hash = portfolioId ? 
    portfolioId.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 
    Math.floor(Math.random() * 1000);
  
  const randomFactor = (hash % 20) / 10;
  
  return {
    totalValue: (100000 * (1 + (randomFactor - 1) * 0.2)).toFixed(2),
    pnl: {
      daily: ((randomFactor - 1) * 2000).toFixed(2),
      weekly: ((randomFactor - 1) * 4500).toFixed(2),
      monthly: ((randomFactor - 1) * 12000).toFixed(2),
      yearly: ((randomFactor - 1) * 35000).toFixed(2)
    },
    allocations: {
      stocks: (40 + (hash % 20)).toFixed(2),
      crypto: (15 + (hash % 10)).toFixed(2),
      forex: (10 + (hash % 8)).toFixed(2),
      commodities: (5 + (hash % 5)).toFixed(2),
      cash: (30 - (hash % 15)).toFixed(2)
    },
    exposures: {
      net: ((randomFactor - 1) * 70).toFixed(2),
      gross: (90 + (hash % 20)).toFixed(2),
      long: (70 + (hash % 20)).toFixed(2),
      short: (20 + (hash % 15)).toFixed(2)
    }
  };
};

const getMockPositionRisks = (portfolioId) => {
  const positions = [
    { symbol: 'AAPL', type: 'stock', sector: 'Technology' },
    { symbol: 'MSFT', type: 'stock', sector: 'Technology' },
    { symbol: 'GOOGL', type: 'stock', sector: 'Technology' },
    { symbol: 'AMZN', type: 'stock', sector: 'Consumer' },
    { symbol: 'BTC', type: 'crypto', sector: 'Crypto' },
    { symbol: 'ETH', type: 'crypto', sector: 'Crypto' },
    { symbol: 'EURUSD', type: 'forex', sector: 'Currency' },
    { symbol: 'GLD', type: 'commodity', sector: 'Metals' }
  ];
  
  return positions.map(position => {
    const hash = (position.symbol + (portfolioId || "")).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const randomFactor = (hash % 20) / 10;
    const isLong = hash % 3 !== 0;
    
    return {
      ...position,
      position: isLong ? 'long' : 'short',
      size: (5000 * randomFactor).toFixed(2),
      allocation: ((randomFactor / 5) * 100).toFixed(2),
      unrealizedPnl: ((randomFactor - 1) * 1000).toFixed(2),
      valueAtRisk: ((randomFactor * 2) * 100).toFixed(2),
      riskContribution: ((randomFactor / 2) * 100).toFixed(2),
      stopLoss: {
        price: ((100 - randomFactor * 5) * (isLong ? 0.9 : 1.1)).toFixed(2),
        distance: ((randomFactor * 5) + 2).toFixed(2),
        type: hash % 2 === 0 ? 'fixed' : 'trailing'
      },
      maxDrawdown: ((randomFactor * 10) + 5).toFixed(2),
      betaToSPY: (randomFactor * 0.8).toFixed(2),
      volatility: ((randomFactor * 5) + 10).toFixed(2),
      correlations: {
        marketIndex: ((randomFactor - 0.5) * 1.2).toFixed(2),
        sectorIndex: ((randomFactor * 0.6) + 0.2).toFixed(2)
      }
    };
  });
};

const getMockRiskMetrics = (portfolioId) => {
  const hash = portfolioId ? 
    portfolioId.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 
    Math.floor(Math.random() * 1000);
  
  const randomFactor = (hash % 20) / 10;
    
  return {
    valueAtRisk: {
      daily: {
        amount: ((randomFactor * 2) * 1000).toFixed(2),
        percent: ((randomFactor * 2) + 0.5).toFixed(2)
      },
      weekly: {
        amount: ((randomFactor * 3) * 1000).toFixed(2),
        percent: ((randomFactor * 2) + 1.2).toFixed(2)
      },
      monthly: {
        amount: ((randomFactor * 5) * 1000).toFixed(2),
        percent: ((randomFactor * 2) + 2.0).toFixed(2)
      }
    },
    expectedShortfall: {
      amount: ((randomFactor * 3) * 1000).toFixed(2),
      percent: ((randomFactor * 2) + 1.0).toFixed(2)
    },
    sharpeRatio: (0.8 + randomFactor * 0.7).toFixed(2),
    sortinoRatio: (1.2 + randomFactor * 0.8).toFixed(2),
    beta: (0.9 + randomFactor * 0.3).toFixed(2),
    alpha: ((randomFactor - 1) * 2).toFixed(2),
    maxDrawdown: {
      current: ((randomFactor * 5) + 5).toFixed(2),
      historical: ((randomFactor * 10) + 15).toFixed(2)
    },
    stressTestResults: {
      marketCrash: (-15 - randomFactor * 10).toFixed(2),
      interestRateHike: (-5 - randomFactor * 5).toFixed(2),
      commoditySurge: ((randomFactor - 1) * 10).toFixed(2),
      volatilitySpike: (-8 - randomFactor * 7).toFixed(2)
    },
    riskExposures: {
      sectors: [
        { name: 'Technology', allocation: (30 + (hash % 15)).toFixed(2), risk: (8 + randomFactor * 4).toFixed(2) },
        { name: 'Financial', allocation: (15 + (hash % 10)).toFixed(2), risk: (7 + randomFactor * 3).toFixed(2) },
        { name: 'Healthcare', allocation: (12 + (hash % 8)).toFixed(2), risk: (5 + randomFactor * 2).toFixed(2) },
        { name: 'Consumer', allocation: (10 + (hash % 7)).toFixed(2), risk: (4 + randomFactor * 2).toFixed(2) },
        { name: 'Energy', allocation: (8 + (hash % 5)).toFixed(2), risk: (9 + randomFactor * 5).toFixed(2) },
        { name: 'Others', allocation: (25 - (hash % 10)).toFixed(2), risk: (6 + randomFactor * 3).toFixed(2) }
      ],
      geographies: [
        { name: 'North America', allocation: (60 + (hash % 10)).toFixed(2), risk: (7 + randomFactor * 3).toFixed(2) },
        { name: 'Europe', allocation: (20 + (hash % 8)).toFixed(2), risk: (8 + randomFactor * 4).toFixed(2) },
        { name: 'Asia', allocation: (15 + (hash % 5)).toFixed(2), risk: (10 + randomFactor * 5).toFixed(2) },
        { name: 'Others', allocation: (5 + (hash % 3)).toFixed(2), risk: (9 + randomFactor * 4).toFixed(2) }
      ]
    }
  };
};

const getMockRiskSettings = (portfolioId) => {
  return {
    general: {
      maxPositionSize: "5%",
      maxSectorExposure: "30%",
      maxDrawdownTolerance: "15%",
      leverageLimit: "2x",
      volatilityTarget: "12%",
      rebalancingFrequency: "Weekly"
    },
    stopLoss: {
      defaultType: "Fixed",
      defaultDistance: "5%",
      trailingStopActivation: "3%",
      stopLossRequired: true
    },
    alerts: {
      portfolioValueDrop: "5%",
      positionValueDrop: "10%",
      volatilityIncrease: "30%",
      correlationThreshold: "0.8"
    },
    riskLimits: {
      valueAtRiskLimit: "3%",
      expectedShortfallLimit: "5%",
      leverageWarning: "1.8x",
      concentrationWarning: "25%"
    }
  };
};

const getMockRiskAlerts = (portfolioId) => {
  // Generate 0-3 alerts based on hash
  const hash = portfolioId ? 
    portfolioId.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 
    Math.floor(Math.random() * 1000);
  
  const alertCount = hash % 4;
  
  const possibleAlerts = [
    {
      id: 1,
      type: 'position_size',
      severity: 'high',
      title: 'Position Size Limit Exceeded',
      description: 'AAPL position exceeds 5% portfolio allocation limit (currently 7.2%)',
      timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
      status: 'active'
    },
    {
      id: 2,
      type: 'sector_exposure',
      severity: 'medium',
      title: 'High Sector Concentration',
      description: 'Technology sector exposure approaching limit (currently 28.5% of 30%)',
      timestamp: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
      status: 'active'
    },
    {
      id: 3,
      type: 'volatility',
      severity: 'medium',
      title: 'Portfolio Volatility Increasing',
      description: 'Portfolio volatility has increased to 15.3% (target: 12%)',
      timestamp: new Date(Date.now() - 1000 * 60 * 240).toISOString(),
      status: 'active'
    },
    {
      id: 4,
      type: 'correlation',
      severity: 'low',
      title: 'High Correlation Between Assets',
      description: 'Multiple tech positions showing correlation > 0.85',
      timestamp: new Date(Date.now() - 1000 * 60 * 480).toISOString(),
      status: 'active'
    },
    {
      id: 5,
      type: 'stop_loss',
      severity: 'high',
      title: 'Stop Loss Triggered',
      description: 'Stop loss triggered for MSFT position at $305.42',
      timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
      status: 'pending'
    }
  ];
  
  // Return 0-3 alerts based on hash
  return possibleAlerts.slice(0, alertCount);
};

// This service would handle real communication with the nodes in a production app
const RiskManagerService = {
  getRiskAnalysis: async (portfolioId = null) => {
    try {
      return await fetchRiskData(portfolioId);
    } catch (error) {
      console.error('Error fetching risk data:', error);
      throw error;
    }
  },
  
  updateRiskSettings: async (settings) => {
    try {
      // In a real app, this would send the settings to the backend
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true, message: 'Risk settings updated successfully' };
    } catch (error) {
      console.error('Error updating risk settings:', error);
      throw error;
    }
  },
  
  resolveAlert: async (alertId) => {
    try {
      // In a real app, this would send the alert resolution to the backend
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true, message: 'Alert resolved successfully' };
    } catch (error) {
      console.error('Error resolving alert:', error);
      throw error;
    }
  },
  
  runStressTest: async (portfolioId, scenario) => {
    try {
      // In a real app, this would initiate a stress test via the risk manager node
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      // Mock response with stress test results
      const hash = portfolioId ? 
        portfolioId.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 
        Math.floor(Math.random() * 1000);
      
      const randomFactor = (hash % 20) / 10;
      
      return {
        success: true,
        scenario,
        impactPercentage: (-10 - randomFactor * 8).toFixed(2),
        impactAmount: (-5000 - randomFactor * 4000).toFixed(2),
        worstPositions: [
          { symbol: 'AAPL', impact: (-12 - randomFactor * 8).toFixed(2) },
          { symbol: 'MSFT', impact: (-9 - randomFactor * 7).toFixed(2) },
          { symbol: 'BTC', impact: (-18 - randomFactor * 10).toFixed(2) }
        ],
        recommendations: [
          "Reduce technology sector exposure",
          "Consider hedging market risk with VIX derivatives",
          "Add uncorrelated assets to reduce drawdown risk"
        ]
      };
    } catch (error) {
      console.error('Error running stress test:', error);
      throw error;
    }
  }
};

export default RiskManagerService;
